<!-- src/apps/secret/reveal/UnknownSecret.vue -->

<script setup lang="ts">
  import UnknownSecretHelpContent from '@/apps/secret/components/UnknownSecretHelpContent.vue';
  import type { BrandSettings } from '@/schemas/shapes/v3/custom-domain';
  import CornerBrackets from '@/shared/components/ui/CornerBrackets.vue';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  // Terminal "gone" state from the Figma Make mock (BurnedState): fading grid
  // backdrop, red mono label, oversized mono heading and a create-new CTA.
  // Copy stays honest — a 404 can mean viewed, expired OR never existed, so it
  // never claims the secret was "destroyed from our servers".
  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();
  const helpEnabled = computed(() => bootstrapStore.ui?.help?.enabled ?? true);

  interface Props {
    branded?: boolean;
    brandSettings?: BrandSettings;
  }

  defineProps<Props>();
</script>

<template>
  <!-- prettier-ignore-attribute class -->
  <div
    class="relative overflow-hidden border border-day-line bg-day px-6 py-14
      dark:border-white/[0.07] dark:bg-night-100 sm:py-20"
    data-testid="unknown-secret">
    <!-- Fading grid backdrop -->
    <!-- prettier-ignore-attribute class -->
    <div
      class="pointer-events-none absolute inset-0
        bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)]
        bg-[size:48px_48px]
        [mask-image:radial-gradient(ellipse_at_50%_50%,black_20%,transparent_70%)]
        dark:bg-[linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)]"
      aria-hidden="true"></div>

    <div class="relative z-10 mx-auto flex max-w-md flex-col items-center text-center">
      <p class="mb-5 font-mono text-[9px] uppercase tracking-[0.14em] text-red-600 dark:text-red-400">
        — {{ t('reveal_terminal.gone_label') }} —
      </p>

      <!-- prettier-ignore-attribute class -->
      <h1
        class="mb-3.5 font-mono text-[clamp(26px,4vw,40px)] font-bold leading-[1.1]
          tracking-[-0.03em] text-day-ink dark:text-night-ink">
        {{ t('web.secrets.that_information_is_no_longer_available') }}
      </h1>

      <p class="mb-9 text-sm font-light leading-[1.65] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('reveal_terminal.gone_note') }}
      </p>

      <div class="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
        <!-- prettier-ignore-attribute class -->
        <router-link
          to="/"
          class="inline-flex items-center justify-center gap-2 bg-brand-600 px-6 py-3
            font-mono text-[11px] uppercase tracking-[0.08em] text-white
            transition-colors hover:bg-brand-700
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            dark:focus:ring-offset-night-100"
          :aria-label="t('web.secrets.create_a_secret')">
          <span aria-hidden="true">←</span>
          {{ t('web.secrets.create_a_secret') }}
        </router-link>

        <!-- prettier-ignore-attribute class -->
        <router-link
          to="/"
          class="inline-flex items-center justify-center border border-day-line-2 bg-day px-6 py-3
            font-mono text-[11px] uppercase tracking-[0.08em] text-day-ink-muted
            transition-colors hover:border-brand-500/40 hover:text-day-ink
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink-muted
            dark:hover:border-brand-500/40 dark:hover:text-night-ink
            dark:focus:ring-offset-night-100"
          :aria-label="t('web.layout.return_to_home_page')">
          {{ t('web.layout.return_to_home') }}
        </router-link>
      </div>

      <!-- Help Modal Trigger -->
      <NeedHelpModal
        v-if="helpEnabled"
        link-icon-name="question-mark-circle-16-solid"
        link-text-label=""
        aria-haspopup="dialog">
        <!-- prettier-ignore-attribute class -->
        <button
          type="button"
          class="mt-8 text-sm font-medium
            text-brand-600 hover:text-brand-700
            focus:underline focus:outline-none dark:text-brand-500 dark:hover:text-brand-400"
          :aria-label="t('web.COMMON.open_help_dialog')">
          {{ t('web.COMMON.need_help') }}
        </button>
        <template #content>
          <UnknownSecretHelpContent />
        </template>
      </NeedHelpModal>
    </div>

    <CornerBrackets />
  </div>
</template>
